import React, { useState } from 'react';
import { Button, Col, Form, List, Modal, Row, message } from 'antd';
import { BookyInput, InputType } from '../Forms/BookyInput';
import { DollarCircleOutlined } from '@ant-design/icons';
import { TextService } from '../../Services/TextService';
import { WebApiModelsExternalPayment, WebApiModelsPayment, WebApiMemberDtoMemberDto, WebApiModelsMembershipProduct } from '../../Repository/eventbookyapi';
import { MemberService } from '../../Services/MemberService';
import { BookyButton } from '../Buttons/BookyButton';
import { BookySelectDropDown } from '../Forms/BookySelectDropDown';
import { getLastThreeYearAsOptions } from '../../Helpers/PaymentYearOptions';
import { formatDateString, formatToShortFormat, getDateStringFromDate, getPreviousYear } from '../../Helpers/DateHelpers';
import { OneFrontDatePicker } from '../Forms/BookyDatePicker';
import moment from 'moment';
import Table, { ColumnsType } from "antd/lib/table";
import AddPaymentLink from './AddPaymentLink';
import { IntegrationType } from '../../Types/IntegrationType';
import { AuthenticatedUser } from '../../Types/AuthenticatedUser';

interface AddPaymentProps {
    member: WebApiMemberDtoMemberDto;
    membershipProducts: WebApiModelsMembershipProduct[] | undefined;
    loadMembers: any;
}

export const AddPayment = (props: AddPaymentProps) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const GetText = TextService.GetText;
    const [payments, setPayments] = useState([] as WebApiModelsPayment[]);
    const [externalPayments, setExternalPayments] = useState([] as WebApiModelsExternalPayment[]);

    const onCancel = () => {
        form.setFieldValue("groupName", "");
        setOpen(false);
        props.loadMembers();
    };

    const onCreate = async () => {

        try {
            await MemberService.SaveOrUpdatePayment({
                memberId: props.member.memberId,
                year: form.getFieldValue("year").toString(),
                paymentDate: new Date(form.getFieldValue("paymentDate").format('YYYY-MM-DD')),
                amount: form.getFieldValue("amount")
            });

            form.setFieldValue("text", "");
            loadPayments(false);
        } catch (error) {
            message.warning(GetText("EditMember_ErrorSaving"));
            return;
        }
    }

    const loadPayments = (isFirstLoading: boolean) => {

        if (isFirstLoading) {
            form.setFieldValue("year", getPreviousYear(0));
        }

        MemberService.GetPayments(props.member.memberId!).then(result => {
            setPayments(result.manualPayments!);
            setExternalPayments(result.externalPayments!);

            result.manualPayments!.forEach((element: WebApiModelsPayment) => {
                if (element.year == form.getFieldValue("year")) {
                    form.setFieldValue("amount", element.amount);
                    form.setFieldValue("paymentDate", moment(element.paymentDate));
                }
            });
        });
    }

    const deletePayment = (payment: WebApiModelsPayment) => {
        MemberService.DeletePayment(payment.id!).then(result => {
            loadPayments(false);
        })
    }

    const onYearChanged = (selectedYear: string) => {
        var yearFound = false;
        payments.forEach((element: WebApiModelsPayment) => {
            if (element.year == selectedYear) {
                yearFound = true;
                form.setFieldValue("amount", element.amount);
                form.setFieldValue("paymentDate", moment(element.paymentDate));
            }
        });

        if (!yearFound) {
            form.setFieldValue("amount", "");
            form.setFieldValue("paymentDate", null);
        }
    }

    const locale = {
        emptyText: GetText("AddPayment_NoPayments")
    }

    const columns: ColumnsType<WebApiModelsExternalPayment> = []

    const isPingPayments = externalPayments.some((externalPayment: WebApiModelsExternalPayment) => externalPayment.integrationType == IntegrationType.PingPayments);
    const isFortnox = externalPayments.some((externalPayment: WebApiModelsExternalPayment) => externalPayment.integrationType == IntegrationType.Fortnox);

    columns.push({
        title: () => { return <span>Betalt</span> },
        dataIndex: 'customerName',
        key: 'customerName',
        render: (_, externalPayment: WebApiModelsExternalPayment) => (
            <Row>
                <Col span={18}>
                    <span><b>{formatDateString(externalPayment.payDate)}</b><br />
                        <br></br>
                    </span>
                </Col>
            </Row>
        ),
    });

    columns.push({
        title: () => { return <span>Integration</span> },
        dataIndex: 'integration',
        key: 'integration',
        render: (_, externalPayment: WebApiModelsExternalPayment) => (
            <Row>
                <Col span={18}>
                    <span><b>{externalPayment.integrationType == IntegrationType.Fortnox ? "Fortnox" : "PingPayments"}</b><br />
                        <br></br>
                    </span>
                </Col>
            </Row>
        ),
    });

    {
        isFortnox && columns.push({
            title: () => { return <span>Sista betaldag</span> },
            dataIndex: 'customerName',
            key: 'customerName',
            render: (_, externalPayment: WebApiModelsExternalPayment) => (
                <Row>
                    <Col span={18}>
                        <span><b>{formatDateString(externalPayment.dueDate)}</b><br />
                            <br></br>
                        </span>
                    </Col>
                </Row>
            ),
        });
    }

    {
        isFortnox && columns.push({
            title: () => { return <span>Fakturerat den</span> },
            dataIndex: 'customerName',
            key: 'customerName',
            render: (_, externalPayment: WebApiModelsExternalPayment) => (
                <Row>
                    <Col span={18}>
                        <span><b>{formatDateString(externalPayment.invoiceDate)}</b><br />
                            <br></br>
                        </span>
                    </Col>
                </Row>
            ),
        });
    }

    {
        isPingPayments && columns.push({
            title: () => { return <span>Markerat för utbetalning den</span> },
            dataIndex: 'customerName',
            key: 'customerName',
            render: (_, externalPayment: WebApiModelsExternalPayment) => (
                <Row>
                    <Col span={18}>
                        <span><b>{formatDateString(externalPayment.settledAt)}</b><br />
                            <br></br>
                        </span>
                    </Col>
                </Row>
            ),
        });
    }


    columns.push({
        title: () => { return <span>Belopp</span> },
        dataIndex: 'customerName',
        key: 'customerName',
        render: (_, externalPayment: WebApiModelsExternalPayment) => (
            <Row>
                <Col span={18}>
                    <span><b>{externalPayment.totalAmount + " kr"}</b><br />
                        <br></br>
                    </span>
                </Col>
            </Row>
        ),
    });

    {
        isFortnox && columns.push({
            title: () => { return <span>OCR</span> },
            dataIndex: 'customerName',
            key: 'customerName',
            render: (_, externalPayment: WebApiModelsExternalPayment) => (
                <Row>
                    <Col span={18}>
                        <span><b>{externalPayment.externalId}</b><br />
                            <br></br>
                        </span>
                    </Col>
                </Row>
            ),
        });
    }

    return (
        <>
            <Modal
                open={open}
                title={GetText("AddPayment_ModalTitle") + " - (" + props.member.uniqueId + ") " + props.member.firstName + " " + props.member.lastName}
                width={700}
                okText={GetText("EditMember_ModalClose")}
                onOk={onCancel}
                onCancel={onCancel}
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                <div style={{ marginTop: '16px' }}>
                    {/* External Payments Section */}
                    {externalPayments.length > 0 && (
                        <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
                            <b>Fakturor/betalningar skickade genom integrationer</b>
                            <div style={{ marginTop: '8px' }}>
                                <Table dataSource={externalPayments} size='small' columns={columns} />
                            </div>
                        </div>
                    )}

                    {/* Membership Products Section */}
                    {props.member.membershipProduct && props.member.membershipProduct > 0 && (
                        <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
                            <b>{GetText("MemberDetails_MembershipInfo")}</b>
                            <div style={{ marginTop: '8px' }}>
                                {(() => {
                                    const mainProduct = props.membershipProducts?.find(p => p.id === props.member.membershipProduct);
                                    const addOnProducts = props.member.membershipProductAddOn?.map(id =>
                                        props.membershipProducts?.find(p => p.id === id)
                                    ).filter(Boolean);

                                    return (
                                        <>
                                            {mainProduct && mainProduct.name && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    {mainProduct.name}: {mainProduct.price} kr
                                                </div>
                                            )}
                                            {addOnProducts && addOnProducts.length > 0 && addOnProducts.map((product) => (
                                                <div key={product!.id} style={{ marginBottom: '4px' }}>
                                                    {product!.name}: {product!.price} kr
                                                </div>
                                            ))}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Add Payment Form Section */}
                    <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
                        <b>{GetText("AddPayment_SaveNote")}</b>
                        <div style={{ marginTop: '8px' }}>
                            <Form form={form} colon={false} labelAlign={"left"} labelCol={{ span: 8, offset: 0 }} wrapperCol={{ span: 16 }} >
                                <Col span={17}>
                                    <BookyInput type={InputType.number} label={GetText("AddPayment_NoteColumnPlaceHolder")} onClick={onCreate} name="amount" req placeholder={GetText("AddPayment_NoteColumnPlaceHolder")} />
                                    <BookySelectDropDown label={GetText("AddPayment_YearColumn")} name="year" handleChange={onYearChanged} options={getLastThreeYearAsOptions()} />
                                    <OneFrontDatePicker label={GetText("AddPayment_PaymentDateColumn")} name="paymentDate" placeholder={GetText("AddPayment_PaymentDatePlaceHolder")} />

                                    <Form.Item label={" "}>
                                        <BookyButton
                                            type="primary"
                                            fullWidth={false}
                                            text={GetText("AddPayment_SaveNote")}
                                            onClick={onCreate}
                                        >
                                        </BookyButton>
                                    </Form.Item>


                                </Col>
                            </Form>
                        </div>
                    </div>

                    {/* Payment History Section */}
                    <div>
                        <b>{GetText("AddPayment_PaymentHistory")}</b>
                        <div style={{ marginTop: '8px' }}>
                            <List
                                locale={locale}
                                itemLayout="horizontal"
                                dataSource={payments}
                                renderItem={(item, index) => (
                                    <List.Item extra={<Button size="small" onClick={() => deletePayment(item)}>{GetText("Common_Delete")}</Button>}>
                                        <List.Item.Meta
                                            title={<span>{item.year}</span>}
                                            description={GetText("AddPayment_NoteColumnPlaceHolder") + ": " + item.amount + " | " + GetText("AddPayment_PaymentDateColumn") + ": " + formatToShortFormat(item.paymentDate)}
                                        />
                                    </List.Item>
                                )}
                            />
                        </div>
                    </div>
                </div>
            </Modal>


            <DollarCircleOutlined className={props.member.isPaidCurrentYear ? "" : "not-payed"}
                style={{ fontSize: "20px" }}
                type="primary"
                onClick={() => {
                    setOpen(true);
                    loadPayments(true);
                }}
            >
            </DollarCircleOutlined>
        </>
    );
}

export default AddPayment;